import { AgentStatus } from '../interfaces/Agent';
import User from '../interfaces/User';
import { fetchUsage } from '../services/billingService';
import { getCompanyAgents, updateAgent } from '../services/agentService';
import {
  getCompanyChatAgents,
  updateChatAgent,
} from '../services/chatAgentService';

type AgentRef = {
  id: string;
  type: 'call' | 'chat';
  status: string;
};

export async function enforcePlanAgentLimits(
  currentUser: User | null | undefined
): Promise<void> {
  const companyId = currentUser?.company_id;
  if (!currentUser || !companyId) return;

  const usage = await fetchUsage(companyId);
  const maxAgents = usage?.max_agents;
  if (maxAgents == null || maxAgents < 0) return;

  const [callResult, chatResult] = await Promise.all([
    getCompanyAgents(),
    getCompanyChatAgents(),
  ]);

  const callAgents = (
    ((callResult as { message?: unknown })?.message as Array<{
      id?: string;
      status?: string;
    }>) ?? []
  ).filter(a => !!a?.id);

  const chatAgents = (
    ((chatResult as { message?: unknown })?.message as Array<{
      id?: string;
      status?: string;
    }>) ?? []
  ).filter(a => !!a?.id);

  const totalAgents = callAgents.length + chatAgents.length;
  if (totalAgents <= maxAgents) return;

  const requiredOfflineCount = totalAgents - maxAgents;

  const orderedAgents: AgentRef[] = [
    ...callAgents.map(a => ({
      id: a.id as string,
      type: 'call' as const,
      status: a.status ?? AgentStatus.OFFLINE,
    })),
    ...chatAgents.map(a => ({
      id: a.id as string,
      type: 'chat' as const,
      status: a.status ?? AgentStatus.OFFLINE,
    })),
  ];

  const currentlyOffline = orderedAgents.filter(
    a => a.status !== AgentStatus.ONLINE
  ).length;
  const mustTurnOffline = Math.max(0, requiredOfflineCount - currentlyOffline);
  if (mustTurnOffline === 0) return;

  const onlineCandidates = orderedAgents
    .filter(a => a.status === AgentStatus.ONLINE)
    .reverse()
    .slice(0, mustTurnOffline);

  for (const agent of onlineCandidates) {
    if (agent.type === 'call') {
      await updateAgent(agent.id, { status: AgentStatus.OFFLINE });
    } else {
      await updateChatAgent(agent.id, { status: AgentStatus.OFFLINE });
    }
  }
}
